/**
 * The one place a DENOMINATED count is rendered on the run-overview surface — a caller physically
 * cannot use this component to print a count without its denominator. See `denominated()` in
 * `lib/run-view.ts`, which owns the actual math/formatting; this only lays the parts out.
 *
 * It is not the only place a bare count reaches the screen: `run-phases.ts`'s breakdown lines
 * (`nulls 12 · duplicates 38`, `new 1,180 · updated 0 · deleted 0`, `failed 106 · dropped 72`) compose
 * bare `exact(value)` strings directly, with no denominator of their own. That is a deliberate
 * exception, not a hole in this rule: each breakdown line's own shape names what every number on it
 * is (`nulls`, `duplicates`, …), and the phase's main value — rendered through THIS component,
 * directly above the line — states the denominator once for the whole phase.
 *
 * TWO WORDS, NOT ONE. `unit` names the DENOMINATOR (`of 85,324 returned`); the optional
 * `numeratorUnit` names the VALUE (`84,120 valid`). A caller that passes both gets
 * `84,120 valid of 85,324 returned · 98.6%`, and the phase stack passes both on every row so that
 * reading the first noun down the three rows spells the pipeline out — valid → changed → accepted.
 * That chain is load-bearing: `PhaseStage` prints no phase captions (see its header), so the numerator
 * words are what tell a reader which step each number belongs to. `numeratorUnit` is OPTIONAL because
 * the other caller — `RunsPanel`'s delta cell — sits under a column header that already names its
 * number (`Changed → Accepted`), so a word in the cell would be the header said twice.
 *
 * Three slots hold a WORD instead of a number, and all three exist because the payload cannot express
 * absence:
 *
 * - `counted={false}` — the run has not measured anything yet (`isCounted`). Rendering the payload's
 *   literal `0` here would assert a count the platform has not taken. PROGRESSIVE: it resolves by
 *   waiting, which is why the word is "counting".
 * - `d < 0` — `changed.total` is the `-1` count-query-timeout sentinel, so there is no denominator
 *   to divide by and none to print. `denominated()` returns a null `of` for this.
 * - `n < 0` — the SAME sentinel, arriving as the numerator (Plan's value IS `changed.total`). This
 *   branch is the mirror of the one above and exists for the same reason: `-1` is not a quantity, so
 *   it must never be formatted or divided. It is deliberately NOT routed through `counted={false}`:
 *   that word is temporal, and a settled run whose count query timed out is not counting — it never
 *   will. "counting matched" on a finished run trades an arithmetically absurd string for a
 *   temporally false one. TERMINAL, so it says {@link PLAN_UNMEASURED}, like its denominator twin.
 *
 * TWO LAYOUTS, ONE SET OF PARTS. `variant` picks between them and nothing else changes:
 *
 * - `inline` (default) — the parts run together in one span, separated by literal spaces, all
 *   inheriting the caller's size and weight. This is `RunsPanel`'s delta cell, which is a TABLE CELL:
 *   it has to `truncate` inside a `min-w-0` flex item, and the whole point of the assertion in
 *   `RunDenominated.test.tsx` ("renders exactly as before when no numerator word is given") is that
 *   its `textContent` is one unbroken sentence. Do not "unify" it with `stat`.
 * - `stat` — the phase stack's headline block, and the only place the three parts are typed as three
 *   different things (Figma `500:1295`): a 20px/700 foreground NUMBER, a 12px/500 foreground
 *   NUMERATOR WORD, and a 12px/400 muted DENOMINATOR CLAUSE, laid out on a BASELINE with a 6px gap.
 *   The numerator word at full foreground is the load-bearing part: `valid -> changed -> accepted` is
 *   what replaced the deleted phase captions, and demoting it to the denominator's grey (which is
 *   what a single inherited colour did) is what stopped the chain reading as a chain.
 *
 * `flex-wrap` on the stat row, with a horizontal-only gap: at 312px of usable width an eight-figure
 * count would otherwise push the denominator clause out of the card, and a wrapped second line is a
 * better failure than a clipped one. It never wraps on any observed payload.
 */
import { cn } from '@/_shared/lib/utils';

import { denominated, isMeasured, PLAN_UNMEASURED } from '../lib/run-view';

interface RunDenominatedProps {
  n: number;
  d: number;
  /** Names the DENOMINATOR — the word after `of`. */
  unit: string;
  /**
   * Names the NUMERATOR — the word straight after the value. Optional: omitted, this renders exactly
   * as it did before the word existed, which is what `RunsPanel`'s delta cell wants.
   */
  numeratorUnit?: string;
  /**
   * Whether `n` and `d` are measurements. `false` puts a word in the value slot — see `isCounted`.
   * Defaults to `true` so every settled call site reads exactly as it did before.
   */
  counted?: boolean;
  /** Which of the two layouts — see this file's "TWO LAYOUTS, ONE SET OF PARTS". */
  variant?: 'inline' | 'stat';
  className?: string;
}

/** The stat layout's row: baseline-aligned parts, 6px apart, wrapping rather than overflowing. */
const STAT_ROW = 'flex flex-wrap items-baseline gap-x-1.5';

export function RunDenominated({
  n,
  d,
  unit,
  numeratorUnit,
  counted = true,
  variant = 'inline',
  className,
}: RunDenominatedProps): React.ReactElement {
  const stat = variant === 'stat';

  if (!counted) {
    return (
      <span className={cn('tabular-nums', stat && STAT_ROW, className)}>
        {/* `records`, a LITERAL — not `{unit}`. `unit` names the DENOMINATOR ("of 1,230 returned"),
            and this branch has no denominator yet: that is the whole reason it exists. Interpolating
            it produced "counting returned" on the Extract row and "counting changed" in `RunsPanel`'s
            delta cell — TWO reachable spellings, both awkward, and neither one a thing anybody says.
            (An earlier draft of this comment claimed Extract was the only reachable caller. It is the
            only one inside `PhaseStage`; the list column is the other, and `RunsPanel.test.tsx` pins
            it.) `records` fixes both and, in the list, now matches the sub-line in the SAME ROW, which
            `runSummary` writes as "Counting records." — the two used to differ for no reason. Figma
            has always said "counting records". `unit` stays in the signature for the measured branch. */}
        {stat ? (
          <>
            {/* 14px/400 muted, and 12px/500 muted — the spec types these two words differently from
                each other, and neither of them like a measured value. The word used to inherit the
                wrapper's `font-medium text-foreground` and read as loud as a number. */}
            <span className="text-sm font-normal text-muted-foreground">counting</span>
            <span className="text-xs font-medium text-muted-foreground">records</span>
          </>
        ) : (
          <>
            counting <span className="text-muted-foreground">records</span>
          </>
        )}
      </span>
    );
  }

  // Before `denominated()`, which only guards the DENOMINATOR (`isMeasured(d)`) — it would happily
  // return `exact(-1)` as `value` and `percent(-1, 1230)` as "-0.1%". No `value` and no `unit` here:
  // there is no numerator to print, and `unit` describes the denominator (see the `parts.of === null`
  // branch's own note below), so it would be the only number-word left standing on a row with no
  // number.
  //
  // SCOPE — read before widening this. The word it prints, `PLAN_UNMEASURED` ("plan size unmeasured"),
  // is PLAN-SPECIFIC, but the guard fires on ANY negative `n`, from any caller. Only Plan is MEANT to
  // reach it: Plan's `n` IS `changed.total`, the one field the platform signs with `-1`. Send's `n` is
  // `successful.total`, which the platform never signs. Extract's `n` is `total − invalid.total`, a
  // subtraction of two counts that are each non-negative — so it goes negative only on a payload where
  // `invalid.total` exceeds `total`, which contradicts itself and has never been observed. That, not a
  // guard, is the only thing keeping "plan size unmeasured" off an Extract row, where it would be the
  // wrong sentence about the wrong number. Do NOT widen this branch to cover another phase without
  // giving that phase its own word first; and if Extract's contradiction ever shows up on the wire,
  // the fix belongs there, not in a second `-1` sentinel guard here.
  if (!isMeasured(n)) {
    // `text-sm` explicitly in the stat layout: the wrapper that used to supply a size is gone, and a
    // terminal phrase must not silently fall back to the body's own.
    return (
      <span className={cn('text-muted-foreground tabular-nums', stat && 'text-sm', className)}>{PLAN_UNMEASURED}</span>
    );
  }

  const parts = denominated(n, d, unit);

  return (
    <span className={cn('tabular-nums', stat && STAT_ROW, className)}>
      {/* In `stat` the value is its OWN span so it can be typed (20px/700) and so the flex row has a
          real item to baseline-align; in `inline` it stays a bare text node followed by a literal
          space, which is what keeps that layout's `textContent` one unbroken sentence. */}
      {stat ? <span className="text-xl font-bold text-foreground">{parts.value}</span> : <>{parts.value} </>}
      {/* Its OWN span, not folded into the denominator span below, for one mechanical reason: that
          span's text is asserted verbatim in three test files (`of 1,230 returned · 100.0%`), and
          those assertions are about the denominator clause, not about whether a numerator word
          precedes it.

          In `inline` it is muted, like every other word in that layout. In `stat` it is FULL
          FOREGROUND at 12px/500 — the spec's one deliberate divergence, and the reason is in this
          file's header: `valid -> changed -> accepted` is the pipeline caption, and a caption printed
          in the denominator's grey stops being read as one. */}
      {numeratorUnit === undefined ? null : stat ? (
        <span className="text-xs font-medium text-foreground">{numeratorUnit}</span>
      ) : (
        <span className="text-muted-foreground">{numeratorUnit} </span>
      )}
      {/* `font-normal` is not redundant in `stat`: nothing else on the row sets it back, and this
          clause used to inherit `font-medium` from the wrapper and print at the numerator's weight. */}
      <span className={cn('text-muted-foreground', stat && 'text-xs font-normal')}>
        {parts.of === null ? (
          // `unit` is still dropped here — it describes the DENOMINATOR ("of 1,230 changed"), and
          // there is no denominator left to attach it to. What the sentinel branch reads as depends
          // on the CALLER now: with a `numeratorUnit` it is `0 accepted · plan size unmeasured`, which
          // names the value it does have and declines to name the one it does not. Without one it is
          // the bare `0 · plan size unmeasured` this branch has always rendered. Neither can produce
          // the defect this comment used to warn about — a numerator wearing the denominator's word,
          // e.g. a Failed row of 0 reading "0 changed" — because `unit` reaches neither shape.
          <>· {PLAN_UNMEASURED}</>
        ) : (
          <>
            of {parts.of} {parts.unit}
            {parts.percent !== null ? ` · ${parts.percent}` : ''}
          </>
        )}
      </span>
    </span>
  );
}
